import React, { useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { useSwipeToDismiss } from '../../app/hooks/useSwipeToDismiss';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
interface SlideOverProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg';
}
export function SlideOver({
  open,
  onClose,
  title,
  description,
  children,
  width = 'md'
}: SlideOverProps) {
  const widthClass = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg'
  }[width];
  useEscapeKey(onClose, open);
  useBodyScrollLock(open);
  // Dit paneel had Escape en een scrollvergrendeling, maar was voor
  // hulpsoftware geen dialoog: geen `role`, geen `aria-modal`, geen
  // focusvang. Wie met een schermlezer of alleen het toetsenbord werkt,
  // tabde er dus doorheen naar de pagina erachter zonder te merken dat er
  // een paneel openstond. Dat raakt de kernhandelingen van het portaal —
  // leerling toevoegen, huiswerk en toetsen klaarzetten lopen hier allemaal
  // doorheen.
  const focusTrapRef = useFocusTrap(open);
  const titleId = useId();
  const descriptionId = useId();
  const swipe = useSwipeToDismiss({
    onDismiss: onClose,
    direction: 'right',
    disabled: !open,
    // Right-slide is x-axis only with dragDirectionLock, so the whole
    // panel can listen without hijacking vertical scrolling.
    wholePanelDraggable: true
  });
  return <AnimatePresence data-id="element-9177" data-mp="1tbl8gzcpcx7k">
      {open && <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-9178" data-mp="1oantswqwilat">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-9179" data-mp="3nni3f2acsl7d" />
        

          {/* Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} {...swipe.dragProps} ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} className={`absolute inset-y-0 right-0 flex flex-col w-full ${widthClass} bg-white shadow-2xl`} data-id="element-9180" data-mp="1lppv1ggil151">
          
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0" data-id="element-9181" data-mp="1pscotmwqww4v">
              <div data-id="element-9182" data-mp="1upmodvdumo6f">
                <h2 id={titleId} className="text-base font-semibold text-gray-900" data-id="element-9183" data-mp="1up34i94n1ppr">
                
                  {title}
                </h2>
                {description && <p id={descriptionId} className="text-sm text-gray-500 mt-0.5" data-id="element-9184" data-mp="jbr8gkf4ebe5">
                
                    {description}
                  </p>}
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0 ml-4" aria-label="Sluiten" data-id="element-9185" data-mp="2sk5pom0qmczv">
              
                <X className="w-5 h-5" data-id="element-9186" data-mp="32id4ozbgukz8" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-5" data-id="element-9187" data-mp="2esit0l2vv2gk">{children}</div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}